import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Shirt,
  Camera,
  CalendarDays,
  FileDown,
  Gem,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LanguageSelector } from '@/components/outfit/LanguageSelector';
import { SponsorBadge } from '@/components/SponsorBadge';

export const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-dvh bg-background flex flex-col relative overflow-hidden">
      {/* ── Decorative background elements ── */}
      <div aria-hidden="true" className="pointer-events-none fixed inset-0">
        <div className="absolute -top-[20%] -left-[15%] w-[60vw] h-[60vw] max-w-[40rem] max-h-[40rem] rounded-full bg-primary/8 blur-[100px]" />
        <div className="absolute -bottom-[10%] -right-[10%] w-[50vw] h-[50vw] max-w-[35rem] max-h-[35rem] rounded-full bg-accent/15 blur-[80px]" />
        <div className="absolute top-[40%] left-[50%] -translate-x-1/2 -translate-y-1/2 w-[30vw] h-[30vw] max-w-[20rem] max-h-[20rem] rounded-full bg-secondary blur-[60px] opacity-60" />
      </div>

      {/* ── Nav bar ── */}
      <header className="sticky top-0 z-30 bg-white/70 backdrop-blur-xl border-b border-border/60">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#E91E8C] to-primary flex items-center justify-center shadow-md shadow-primary/20">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-serif italic text-lg font-bold text-foreground tracking-wide">
              {t('app.title')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <Button
              onClick={() => navigate('/login')}
              variant="ghost"
              className="rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground px-4 hidden sm:inline-flex"
            >
              {t('auth.login')}
            </Button>
            <Button
              onClick={() => navigate('/login')}
              className="rounded-full bg-gradient-to-r from-[#E91E8C] to-primary text-white font-bold text-sm px-5 shadow-md shadow-primary/20 hover:opacity-90 active:scale-95 transition-all"
            >
              {t('auth.register')}
            </Button>
          </div>
        </div>
      </header>

      {/* ── Hero section ── */}
      <section className="relative z-10 flex-1 flex flex-col items-center justify-center px-5 pt-16 pb-12 sm:pt-24 sm:pb-20">
        <div className="max-w-3xl mx-auto text-center animate-spring-in">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-accent" />
            <div className="w-2 h-2 rounded-full bg-primary/60" />
            <Sparkles className="w-5 h-5 text-primary" />
            <div className="w-2 h-2 rounded-full bg-primary/60" />
            <div className="w-1.5 h-1.5 rounded-full bg-accent" />
          </div>

          <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-[1.1] tracking-tight mb-5">
            {t('landing.heroTitle')}
          </h1>

          <p className="text-lg sm:text-xl text-muted-foreground font-medium max-w-xl mx-auto leading-relaxed mb-10">
            {t('landing.heroSubtitle')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              onClick={() => navigate('/login')}
              className="h-13 px-8 rounded-full bg-gradient-to-r from-[#E91E8C] to-primary text-white font-bold text-base shadow-lg shadow-primary/25 hover:opacity-90 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/30 active:scale-95 active:translate-y-0 transition-all"
            >
              {t('landing.cta')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-xs text-muted-foreground/60 font-medium">
              {t('landing.noCreditCard')}
            </p>
          </div>
        </div>

        {/* ── Mock UI preview ── */}
        <div
          className="mt-14 sm:mt-20 w-full max-w-4xl mx-auto animate-spring-in"
          style={{ animationDelay: '200ms', animationFillMode: 'both' }}
        >
          <div className="bg-white rounded-3xl border border-border shadow-2xl shadow-primary/8 p-5 sm:p-8 relative overflow-hidden">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary text-white flex items-center justify-center font-bold text-sm shadow-md shadow-primary/20">
                  1
                </div>
                <div>
                  <p className="font-serif italic font-bold text-foreground text-sm">
                    {t('outfit.day')} 1
                  </p>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Cannes Film Festival
                  </p>
                </div>
              </div>
              <div className="flex gap-1.5">
                {[1, 2, 3, 4, 5].map((d) => (
                  <div
                    key={d}
                    className={`w-7 h-7 rounded-lg text-xs font-bold flex items-center justify-center ${
                      d === 1
                        ? 'bg-primary text-white'
                        : d <= 3
                        ? 'bg-secondary text-primary'
                        : 'bg-background text-muted-foreground border border-border'
                    }`}
                  >
                    {d}
                  </div>
                ))}
                <div className="w-7 h-7 rounded-lg text-[10px] font-bold flex items-center justify-center bg-background text-muted-foreground border border-border">
                  ...
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: Shirt, label: t('outfit.title'), count: '2/4', active: true },
                { icon: Gem, label: t('outfit.jewelry'), count: '3/4', active: true },
                { icon: Camera, label: t('outfit.shoes'), count: '1/2', active: true },
                { icon: Sparkles, label: t('outfit.hair'), count: '0/2', active: false },
              ].map((card, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3.5 rounded-2xl border border-border bg-white hover:shadow-sm transition-shadow"
                >
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      card.active
                        ? 'bg-gradient-to-br from-[#E91E8C] to-primary text-white shadow-md shadow-primary/20'
                        : 'bg-secondary text-primary'
                    }`}
                  >
                    <card.icon className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <p className="font-serif italic text-sm font-bold text-foreground">
                      {card.label}
                    </p>
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                      {card.count} foto
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
          </div>
        </div>
      </section>

      {/* ── Features section ── */}
      <section className="relative z-10 py-16 sm:py-24 px-5">
        <div className="max-w-5xl mx-auto">
          <div
            className="text-center mb-12 animate-spring-in"
            style={{ animationDelay: '100ms', animationFillMode: 'both' }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">
              {t('landing.featuresTag')}
            </p>
            <h2 className="font-serif italic text-3xl sm:text-4xl font-bold text-foreground">
              {t('landing.featuresTitle')}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: CalendarDays, title: t('landing.feat1Title'), desc: t('landing.feat1Desc') },
              { icon: Camera, title: t('landing.feat2Title'), desc: t('landing.feat2Desc') },
              { icon: Shirt, title: t('landing.feat3Title'), desc: t('landing.feat3Desc') },
              { icon: FileDown, title: t('landing.feat4Title'), desc: t('landing.feat4Desc') },
              { icon: Sparkles, title: t('landing.feat5Title'), desc: t('landing.feat5Desc') },
              { icon: Gem, title: t('landing.feat6Title'), desc: t('landing.feat6Desc') },
            ].map((feat, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-border p-6 hover:shadow-[0_12px_32px_rgba(194,24,91,0.08)] hover:-translate-y-1 transition-all duration-300 group animate-spring-in"
                style={{ animationDelay: `${150 + i * 80}ms`, animationFillMode: 'both' }}
              >
                <div className="w-11 h-11 rounded-xl bg-secondary flex items-center justify-center mb-4 group-hover:bg-gradient-to-br group-hover:from-[#E91E8C] group-hover:to-primary transition-all duration-300">
                  <feat.icon className="w-5 h-5 text-primary group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-serif italic text-base font-bold text-foreground mb-2">
                  {feat.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="relative z-10 py-16 sm:py-20 px-5 bg-white/50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">
              {t('landing.howTag')}
            </p>
            <h2 className="font-serif italic text-3xl sm:text-4xl font-bold text-foreground">
              {t('landing.howTitle')}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-10">
            {[
              { step: '01', title: t('landing.step1Title'), desc: t('landing.step1Desc') },
              { step: '02', title: t('landing.step2Title'), desc: t('landing.step2Desc') },
              { step: '03', title: t('landing.step3Title'), desc: t('landing.step3Desc') },
            ].map((step, i) => (
              <div key={i} className="text-center sm:text-left">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-secondary text-primary font-bold text-lg mb-4 font-serif italic">
                  {step.step}
                </div>
                <h3 className="font-serif italic text-lg font-bold text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Sponsor section ── */}
      <section className="relative z-10 py-14 px-5">
        <div className="max-w-md mx-auto text-center">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground mb-5">
            {t('landing.sponsoredBy')}
          </p>
          <a
            href="https://beddamatri.shop/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-col items-center gap-3 group"
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200/60 flex items-center justify-center shadow-sm group-hover:shadow-md group-hover:-translate-y-0.5 transition-all">
              <span className="text-2xl" aria-hidden="true">&#10022;</span>
            </div>
            <div>
              <p className="font-serif italic text-2xl font-bold text-foreground group-hover:text-primary transition-colors">
                Bedda Matri
              </p>
              <p className="text-sm text-muted-foreground mt-1 italic">
                "Dal cuore della Sicilia, con amore"
              </p>
              <p className="text-xs text-muted-foreground/60 mt-1">
                Gioielli siciliani artigianali
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-primary/70 group-hover:text-primary transition-colors mt-1">
              beddamatri.shop
              <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
            </span>
          </a>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="relative z-10 py-16 sm:py-20 px-5">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif italic text-3xl sm:text-4xl font-bold text-foreground mb-4">
            {t('landing.ctaTitle')}
          </h2>
          <p className="text-muted-foreground text-base mb-8 max-w-md mx-auto">
            {t('landing.ctaDesc')}
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="h-13 px-10 rounded-full bg-gradient-to-r from-[#E91E8C] to-primary text-white font-bold text-base shadow-lg shadow-primary/25 hover:opacity-90 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/30 active:scale-95 transition-all"
          >
            {t('landing.ctaButton')}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="relative z-10 border-t border-border bg-white/40 py-6 px-5">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="font-serif italic text-sm font-bold text-foreground">
              {t('app.title')}
            </span>
          </div>
          <SponsorBadge variant="inline" />
          <p className="text-xs text-muted-foreground/50 font-medium">
            &copy; {new Date().getFullYear()} Cannes Outfit Planner
          </p>
        </div>
      </footer>
    </div>
  );
};
